import React, { useState, useEffect } from "react";
import UserService from "../services/user.service";
import { Alert, Table, Button, Container, Form, Pagination, Spinner } from 'react-bootstrap';
import { FaTrash } from 'react-icons/fa';

const InvoiceManager = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiErrors, setApiErrors] = useState('');
  const [apiSuccess, setApiSuccess] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('invoice_number');
  const [sortOrder, setSortOrder] = useState('asc');

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <i className="fas fa-hourglass-half" style={{ color: 'orange' }} title="Ausstehend"></i>;
      case 'paid':
        return <i className="fas fa-check-circle" style={{ color: 'green' }} title="Bezahlt"></i>;
      case 'canceled':
        return <i className="fas fa-times-circle" style={{ color: 'red' }} title="Storniert"></i>;
      case 'overdue':
        return <i className="fas fa-exclamation-circle" style={{ color: 'red' }} title="Überfällig"></i>;
      case 'refunded':
        return <i className="fas fa-undo-alt" style={{ color: 'blue' }} title="Erstattet"></i>;
      default:
        return <i className="fas fa-question-circle" style={{ color: 'gray' }} title="Unbekannter Status"></i>;
    }
  };
  
  const translateStatus = (status) => {
    switch (status) {
      case 'pending': return 'Ausstehend';
      case 'paid': return 'Bezahlt';
      case 'canceled': return 'Storniert';
      default: return status;
    }
  };

  useEffect(() => {
    loadInvoices();
  }, [currentPage, searchTerm, sortField, sortOrder]);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const response = await UserService.getInvoices({
        page: currentPage,
        search: searchTerm,
        sort: sortField,
        order: sortOrder,
      });

      setInvoices(response.data.invoices);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      setApiErrors(`Rechnungen konnten nicht geladen werden: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Beim Suchen auf die erste Seite zurücksetzen
  };

  const handleSortChange = (field) => {
    const newSortOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newSortOrder);
  };

  // Sichtbarer Sortier-Indikator (Pfeil) am aktiven Spaltenkopf.
  const renderSortIndicator = (field) => {
    if (sortField !== field) return <span style={{ opacity: 0.3 }}> ⇅</span>;
    return <span> {sortOrder === 'asc' ? '▲' : '▼'}</span>;
  };

  // Spaltenkopf wirkt klickbar (Cursor + keine Textauswahl beim Klicken).
  const sortableHeaderStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

  const handleCancelInvoice = async (invoiceId) => {
    setLoading(true);
    try {
      await UserService.cancelInvoice(invoiceId);
      loadInvoices(); // Rechnungen nach dem Stornieren neu laden
    } catch (error) {
      setApiErrors(`Rechnung konnte nicht storniert werden: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async (invoiceId) => {
    setLoading(true);
    try {
      await UserService.markInvoiceAsPaid(invoiceId);
      loadInvoices(); // Rechnungen nach dem Bezahlen neu laden
    } catch (error) {
      setApiErrors(`Rechnung konnte nicht als bezahlt markiert werden: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId) => {
    setLoading(true);
    setApiErrors('');
    setApiSuccess('');
    try {
      const response = await UserService.downloadInvoice(invoiceId);

      const contentDisposition = response.headers['content-disposition'];
      let filename = `rechnung_${invoiceId}.pdf`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch && filenameMatch.length > 1) {
          filename = filenameMatch[1];
        }
      }
      
      filename = filename.replace(/(\.pdf)[\W_]+$/, '$1');

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      if (error.response && error.response.status === 404) {
        setApiErrors('Für diese Rechnung wurde noch kein PDF erzeugt. Bitte zuerst auf "PDF erzeugen" klicken.');
      } else {
        setApiErrors(`Rechnung konnte nicht heruntergeladen werden: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInvoicePdf = async (invoiceId) => {
    setLoading(true);
    setApiErrors('');
    setApiSuccess('');
    try {
      const response = await UserService.generateInvoicePdf(invoiceId);
      setApiSuccess(response?.data?.message || 'PDF wurde erfolgreich erzeugt.');
      loadInvoices();
    } catch (error) {
      const serverMessage = error.response?.data?.error || error.message;
      setApiErrors(`PDF konnte nicht erzeugt werden: ${serverMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    setLoading(true);
    try {
      await UserService.deleteInvoice(invoiceId);
      loadInvoices();
    } catch (error) {
      setApiErrors(`Rechnung konnte nicht gelöscht werden: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <h1>Rechnungsverwaltung</h1>
      {apiErrors && <Alert variant="danger" onClose={() => setApiErrors('')} dismissible>{apiErrors}</Alert>}
      {apiSuccess && <Alert variant="success" onClose={() => setApiSuccess('')} dismissible>{apiSuccess}</Alert>}

      <Form.Group controlId="searchInvoice" className="mb-3">
        <Form.Label>Rechnungen durchsuchen</Form.Label>
        <Form.Control
          type="text"
          placeholder="Suche nach Rechnungsnummer oder SubVO-Name..."
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </Form.Group>

      {loading && <div className="text-center"><Spinner animation="border" /></div>}
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th style={sortableHeaderStyle} onClick={() => handleSortChange('invoice_number')}>Rechnungs-Nr.{renderSortIndicator('invoice_number')}</th>
            <th style={sortableHeaderStyle} onClick={() => handleSortChange('sub_vo_name')}>SubVO Name{renderSortIndicator('sub_vo_name')}</th>
            <th style={sortableHeaderStyle} onClick={() => handleSortChange('invoice_total')}>Betrag{renderSortIndicator('invoice_total')}</th>
            <th style={sortableHeaderStyle} onClick={() => handleSortChange('status')}>Status{renderSortIndicator('status')}</th>
            <th style={sortableHeaderStyle} onClick={() => handleSortChange('invoice_date')}>Rechnungsdatum{renderSortIndicator('invoice_date')}</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice) => (
            <tr key={invoice.invoice_id}>
              <td>{invoice.invoice_number}</td>
              <td>{invoice.sub_vo_name}</td>
              <td>{invoice.invoice_total} {invoice.currency}</td>
              <td>{getStatusIcon(invoice.status)} {translateStatus(invoice.status)}</td>
              <td>{new Date(invoice.invoice_date).toLocaleDateString('de-DE')}</td>
              <td>
                <Button
                  className="me-2 mb-1"
                  variant="success"
                  size="sm"
                  disabled={invoice.status !== 'pending'}
                  onClick={() => handleMarkAsPaid(invoice.invoice_id)}
                >
                  Als bezahlt markieren
                </Button>
                <Button
                  className="me-2 mb-1"
                  variant="warning"
                  size="sm"
                  disabled={invoice.status === 'canceled'}
                  onClick={() => handleCancelInvoice(invoice.invoice_id)}
                >
                  Stornieren
                </Button>
                <Button
                  className="me-2 mb-1"
                  variant="info"
                  size="sm"
                  onClick={() => handleGenerateInvoicePdf(invoice.invoice_id)}
                >
                  PDF erzeugen
                </Button>
                <Button
                  className="me-2 mb-1"
                  variant="primary"
                  size="sm"
                  onClick={() => handleDownloadInvoice(invoice.invoice_id)}
                >
                  Herunterladen
                </Button>
                <Button
                  className="mb-1"
                  variant="danger"
                  size="sm"
                  onClick={() => handleDeleteInvoice(invoice.invoice_id)}
                  title="Löschen"
                  aria-label="Löschen"
                >
                  <FaTrash />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Pagination>
        {[...Array(totalPages)].map((_, index) => (
          <Pagination.Item
            key={index}
            active={index + 1 === currentPage}
            onClick={() => setCurrentPage(index + 1)}
          >
            {index + 1}
          </Pagination.Item>
        ))}
      </Pagination>
    </Container>
  );
};

export default InvoiceManager;